import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import Spin from 'pared/components/basicUi/spin'
import COLORS from 'pared/constants/colors'
import { MOBILE_WIDTH } from 'pared/constants/styles'

import usePrepItemCategoryDataSource from '../hooks/usePrepItemCategoryDatasource'
import { getIcon } from '../utils/getIcon'
import {
  IPrepItemBase,
  calculatePrepItemValues,
  formatNonPrepItemValue,
  formatPrepItemValue,
  getNonPrepBackgroundColor,
  getNonPrepTextColor,
  getOnHandUnitLabel,
  getPrepBackgroundColor,
  getPrepTextColor,
  isBucketItem,
} from '../utils/prepItemCalculations'

const PrepGuideContainer = styled.div`
  margin-top: 40px;
  width: 100%;
  min-width: 800px;
  max-width: 1200px;

  @media ${MOBILE_WIDTH} {
    width: 100vw;
    min-width: initial;
    padding: 5px;
    box-sizing: border-box;
  }
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 25px 0;

  @media ${MOBILE_WIDTH} {
    margin-bottom: 20px;
  }
`

const Title = styled.h2`
  margin: 0;
  font-family: Lexend-SemiBold;
  font-size: 24px;
  font-weight: 600;
  color: ${COLORS.Chalkboard};

  @media ${MOBILE_WIDTH} {
    font-size: 20px;
  }
`

const HintText = styled.pre`
  font-family: Lexend-Regular;
  font-size: 14px;
  line-height: 1.6;
  color: ${COLORS.Chalkboard};
  white-space: pre-wrap;
  margin: 0;
`

const ItemCard = styled.div`
  background-color: #f8f8f8;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  width: 100%;
  box-sizing: border-box;

  @media ${MOBILE_WIDTH} {
    padding: 20px 15px;
  }
`

const ItemHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`

const ItemIcon = styled.div<{ color?: string }>`
  width: 40px;
  height: 40px;
  background-color: ${(props) => props.color || '#ff9500'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  color: white;
  font-size: 20px;
`

const ItemInfo = styled.div`
  flex: 1;
`

const ItemName = styled.h3`
  margin: 0;
  font-family: Lexend-SemiBold;
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.Chalkboard};
`

const InputGrid = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-top: 15px;
  flex-wrap: nowrap;

  @media ${MOBILE_WIDTH} {
    flex-direction: column;
    gap: 15px;
  }
`

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;

  &:last-child {
    margin-right: 0;
  }

  @media ${MOBILE_WIDTH} {
    width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`

const InputLabel = styled.label`
  font-family: Lexend-Regular;
  font-size: 12px;
  color: ${COLORS.Steel};
  margin-bottom: 5px;

  @media ${MOBILE_WIDTH} {
    margin-bottom: 0;
    min-width: 80px;
    font-weight: 500;
  }
`

const Input = styled.input<{ backgroundColor?: string }>`
  height: 40px;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  text-align: center;
  width: 100%;
  background-color: ${(props) => props.backgroundColor || 'white'};

  &:focus {
    outline: none;
    border-color: ${COLORS.Plum};
  }
`

const ValueBox = styled.div<{
  backgroundColor?: string
  textColor?: string
  borderColor?: string
}>`
  height: 40px;
  padding: 8px;
  border: 1px solid ${(props) => props.borderColor || '#e0e0e0'};
  background-color: ${(props) => props.backgroundColor || 'white'};
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  color: ${(props) => props.textColor || COLORS.Chalkboard};
  display: flex;
  align-items: center;
  justify-content: center;
`

interface IProps {
  dataSource: IPrepItemBase[]
  nonPrepDataSource?: IPrepItemBase[]
  adjustment?: number
  onHandValues?: Record<string, Record<string, number>>
  onOnHandChange?: (values: Record<string, Record<string, number>>) => void
  disableInput?: boolean
}

const PrepGuide: React.FC<IProps> = ({
  dataSource,
  nonPrepDataSource = [],
  adjustment = 0,
  onHandValues: externalOnHandValues,
  onOnHandChange,
  disableInput,
}) => {
  const [internalOnHandValues, setInternalOnHandValues] = useState<
    Record<string, Record<string, number>>
  >({})

  // Local state for input display values
  const [inputDisplayValues, setInputDisplayValues] = useState<
    Record<string, Record<string, string>>
  >({})

  const onHandValues = externalOnHandValues || internalOnHandValues
  const setOnHandValues = onOnHandChange || setInternalOnHandValues

  // State for hint modals
  const [showPrepGuideHint, setShowPrepGuideHint] = useState(false)
  const [showNonPrepHint, setShowNonPrepHint] = useState(false)
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null)

  // Hint texts
  const prepGuideHintText = `Green cells = Items scheduled to be prepped that day

Parentheses and numbers in the prep cell:
Green cell, no parentheses: You need to prep this item today — you don't have enough.
Green cell, with parentheses: You have enough — no prep needed, even though it was scheduled. This number is how much you will have extra by the next prep day.`

  const nonPrepHintText = `Red cells = Items not scheduled to be prepped that day
Gray cells = Items not scheduled to be prepped that day and you have enough on hand — confirmed no prep needed

Parentheses and numbers in the prep cell:
Red cell, with parentheses: You don't have enough, and although it wasn't scheduled to prep today, you should prep this item. This number is how much you need to prep to get you by until the next prep day.
Red cell, no parentheses: You won’t see this.`

  // Sync input display values with onHandValues
  useEffect(() => {
    const newDisplayValues: Record<string, Record<string, string>> = {}
    Object.keys(onHandValues).forEach((itemName) => {
      newDisplayValues[itemName] = {}
      Object.keys(onHandValues[itemName]).forEach((batchType) => {
        const value = onHandValues[itemName][batchType]
        newDisplayValues[itemName][batchType] = value === 0 ? '' : String(value)
      })
    })
    setInputDisplayValues(newDisplayValues)
  }, [onHandValues])

  const handleOnHandChange = (
    itemName: string,
    batchType: string,
    value: string,
  ) => {
    // Prevent negative numbers - don't update if value starts with minus
    if (value.startsWith('-')) {
      return
    }

    // Update display value immediately
    setInputDisplayValues((prev) => ({
      ...prev,
      [itemName]: {
        ...prev[itemName],
        [batchType]: value,
      },
    }))

    // Handle empty input
    if (value === '') {
      const newValues = {
        ...onHandValues,
        [itemName]: {
          ...onHandValues[itemName],
          [batchType]: 0,
        },
      }
      setOnHandValues(newValues)
      return
    }

    // Allow typing decimal point
    if (value === '.') {
      return
    }

    const numValue = parseFloat(value)
    // Only update if it's a valid positive number
    if (!isNaN(numValue) && numValue >= 0) {
      const newValues = {
        ...onHandValues,
        [itemName]: {
          ...onHandValues[itemName],
          [batchType]: numValue,
        },
      }
      setOnHandValues(newValues)
    }
  }

  const getInputDisplayValue = (itemName: string, batchType: string) => {
    return inputDisplayValues[itemName]?.[batchType] ?? ''
  }

  const getCategoryColor = (category?: string): string => {
    const colors = {
      dip: '#7BB3F0', // Lighter Blue
      sauce: '#E57373', // Lighter Red
      sides: '#FFB74D', // Lighter Orange
      vegetable: '#81C784', // Lighter Green
      'category-brownies': '#FFD700', // Yellow
    }
    return colors[category as keyof typeof colors] || '#757575'
  }

  const {
    dataSource: prepItemCategoryDataSource,
    loading: prepItemCategoryLoading,
  } = usePrepItemCategoryDataSource()

  const [categoryMap, setCategoryMap] = useState<Map<string, string>>(new Map())

  useEffect(() => {
    if (prepItemCategoryDataSource) {
      const map = new Map()
      prepItemCategoryDataSource.forEach((item) => {
        map.set(item.key, item.categoryKey)
      })
      setCategoryMap(map)
    }
  }, [prepItemCategoryDataSource])

  return (
    <PrepGuideContainer>
      <TitleContainer>
        <Title>Prep Guide</Title>
        <IconButton
          size="small"
          onClick={() => setShowPrepGuideHint(true)}
          style={{ color: COLORS.Plum }}
        >
          <InfoOutlinedIcon />
        </IconButton>
      </TitleContainer>

      {dataSource.map((item) => {
        const calculations = calculatePrepItemValues(
          item,
          adjustment,
          onHandValues,
        )
        const onHandUnitLabel = getOnHandUnitLabel(item)

        return (
          <ItemCard key={item.prepItemIdCode}>
            <ItemHeader>
              <Spin spinning={prepItemCategoryLoading}>
                <ItemIcon
                  color={getCategoryColor(categoryMap.get(item.prepItemIdCode))}
                >
                  {getIcon(item.prepItemIdCode)}
                </ItemIcon>
              </Spin>
              <ItemInfo>
                <ItemName
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  {item.prepItemName}
                  {isBucketItem(item.prepItemIdCode) && (
                    <IconButton
                      size="small"
                      onClick={(e) => setInfoAnchorEl(e.currentTarget)}
                      style={{ padding: '2px' }}
                    >
                      <InfoOutlinedIcon style={{ fontSize: '1rem' }} />
                    </IconButton>
                  )}
                </ItemName>
              </ItemInfo>
            </ItemHeader>

            <InputGrid>
              {(() => {
                const details = item.prepOnHandDetails || []
                const columns = []

                // Always show exactly 3 columns
                for (let i = 0; i < 3; i++) {
                  const detail = details[i]
                  if (detail) {
                    columns.push(
                      <InputGroup key={detail.prepBatchType}>
                        <InputLabel>{detail.prepBatchType}</InputLabel>
                        <Input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          value={getInputDisplayValue(
                            item.prepItemName,
                            detail.prepBatchType,
                          )}
                          onChange={(e) =>
                            handleOnHandChange(
                              item.prepItemName,
                              detail.prepBatchType,
                              e.target.value,
                            )
                          }
                          placeholder="0"
                          step="1"
                          disabled={disableInput}
                          backgroundColor={disableInput ? '#f5f5f5' : 'white'}
                        />
                      </InputGroup>,
                    )
                  } else {
                    // Blank column
                    columns.push(
                      <InputGroup key={`blank-${i}`}>
                        <InputLabel>&nbsp;</InputLabel>
                        <Input
                          type="number"
                          value=""
                          disabled
                          placeholder=""
                          backgroundColor="#f5f5f5"
                        />
                      </InputGroup>,
                    )
                  }
                }
                return columns
              })()}

              <InputGroup>
                <InputLabel>On Hand ({onHandUnitLabel})</InputLabel>
                <ValueBox backgroundColor="#f5f5f5" textColor="#666666">
                  {calculations.displayOnHand.toFixed(2)}
                </ValueBox>
              </InputGroup>

              {(() => {
                const isCCItem = isBucketItem(item.prepItemIdCode)
                const neededDisplay = calculations.needed
                const prepDisplay = calculations.prep
                return (
                  <>
                    <InputGroup>
                      <InputLabel>
                        {isCCItem
                          ? 'Needed (Bucket)'
                          : `Needed (${item.prepBatchType})`}
                      </InputLabel>
                      <ValueBox backgroundColor="#e3f2fd" textColor="#1976d2">
                        {neededDisplay.toFixed(1)}
                      </ValueBox>
                    </InputGroup>

                    <InputGroup>
                      <InputLabel>
                        {isCCItem
                          ? 'Prep (Bucket)'
                          : `Prep (${item.prepBatchType})`}
                      </InputLabel>
                      <ValueBox
                        backgroundColor={getPrepBackgroundColor(
                          calculations.prep,
                        )}
                        textColor={getPrepTextColor(calculations.prep)}
                      >
                        {formatPrepItemValue(prepDisplay)}
                      </ValueBox>
                    </InputGroup>
                  </>
                )
              })()}
            </InputGrid>
          </ItemCard>
        )
      })}

      {nonPrepDataSource.length > 0 && (
        <>
          <TitleContainer style={{ marginTop: '60px' }}>
            <Title>Non-Prep Items (Not Scheduled Today)</Title>
            <IconButton
              size="small"
              onClick={() => setShowNonPrepHint(true)}
              style={{ color: COLORS.Plum }}
            >
              <InfoOutlinedIcon />
            </IconButton>
          </TitleContainer>

          {nonPrepDataSource.map((item) => {
            const calculations = calculatePrepItemValues(
              item,
              adjustment,
              onHandValues,
            )
            const onHandUnitLabel = getOnHandUnitLabel(item)

            return (
              <ItemCard
                key={item.prepItemIdCode}
                style={{ backgroundColor: '#fafafa' }}
              >
                <ItemHeader>
                  <Spin spinning={prepItemCategoryLoading}>
                    <ItemIcon
                      color={getCategoryColor(
                        categoryMap.get(item.prepItemIdCode),
                      )}
                    >
                      {getIcon(item.prepItemIdCode)}
                    </ItemIcon>
                  </Spin>
                  <ItemInfo>
                    <ItemName
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      {item.prepItemName}
                      {isBucketItem(item.prepItemIdCode) && (
                        <IconButton
                          size="small"
                          onClick={(e) => setInfoAnchorEl(e.currentTarget)}
                          style={{ padding: '2px' }}
                        >
                          <InfoOutlinedIcon style={{ fontSize: '1rem' }} />
                        </IconButton>
                      )}
                    </ItemName>
                  </ItemInfo>
                </ItemHeader>

                <InputGrid>
                  {(() => {
                    const details = item.prepOnHandDetails || []
                    const columns = []

                    // Always show exactly 3 columns
                    for (let i = 0; i < 3; i++) {
                      const detail = details[i]
                      if (detail) {
                        columns.push(
                          <InputGroup key={detail.prepBatchType}>
                            <InputLabel>{detail.prepBatchType}</InputLabel>
                            <Input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              value={getInputDisplayValue(
                                item.prepItemName,
                                detail.prepBatchType,
                              )}
                              onChange={(e) =>
                                handleOnHandChange(
                                  item.prepItemName,
                                  detail.prepBatchType,
                                  e.target.value,
                                )
                              }
                              placeholder="0"
                              step="1"
                              disabled={disableInput}
                              backgroundColor={
                                disableInput ? '#f5f5f5' : 'white'
                              }
                            />
                          </InputGroup>,
                        )
                      } else {
                        // Blank column
                        columns.push(
                          <InputGroup key={`blank-${i}`}>
                            <InputLabel>&nbsp;</InputLabel>
                            <Input
                              type="number"
                              value=""
                              disabled
                              placeholder=""
                              backgroundColor="#f5f5f5"
                            />
                          </InputGroup>,
                        )
                      }
                    }
                    return columns
                  })()}

                  <InputGroup>
                    <InputLabel>On Hand ({onHandUnitLabel})</InputLabel>
                    <ValueBox backgroundColor="#f5f5f5" textColor="#666666">
                      {calculations.displayOnHand.toFixed(2)}
                    </ValueBox>
                  </InputGroup>

                  {(() => {
                    const isCCItem = isBucketItem(item.prepItemIdCode)
                    const neededDisplay = calculations.needed
                    const prepDisplay = calculations.prep
                    return (
                      <>
                        <InputGroup>
                          <InputLabel>
                            {isCCItem
                              ? 'Needed (Bucket)'
                              : `Needed (${item.prepBatchType})`}
                          </InputLabel>
                          <ValueBox
                            backgroundColor="#f5f5f5"
                            textColor="#999999"
                          >
                            {neededDisplay.toFixed(1)}
                          </ValueBox>
                        </InputGroup>

                        <InputGroup>
                          <InputLabel>
                            {isCCItem
                              ? 'Prep (Bucket)'
                              : `Prep (${item.prepBatchType})`}
                          </InputLabel>
                          <ValueBox
                            backgroundColor={getNonPrepBackgroundColor(
                              calculations.prep,
                            )}
                            textColor={getNonPrepTextColor(calculations.prep)}
                          >
                            {formatNonPrepItemValue(prepDisplay)}
                          </ValueBox>
                        </InputGroup>
                      </>
                    )
                  })()}
                </InputGrid>
              </ItemCard>
            )
          })}
        </>
      )}

      {/* Prep Guide Hint Modal */}
      <Dialog
        open={showPrepGuideHint}
        onClose={() => setShowPrepGuideHint(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Prep Guide Help</DialogTitle>
        <DialogContent>
          <HintText>{prepGuideHintText}</HintText>
        </DialogContent>
      </Dialog>

      {/* Non-Prep Items Hint Modal */}
      <Dialog
        open={showNonPrepHint}
        onClose={() => setShowNonPrepHint(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Non-Prep Items Help</DialogTitle>
        <DialogContent>
          <HintText>{nonPrepHintText}</HintText>
        </DialogContent>
      </Dialog>

      {/* C&C conversion reference popover */}
      <Popover
        open={Boolean(infoAnchorEl)}
        anchorEl={infoAnchorEl}
        onClose={() => setInfoAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div
          style={{ padding: '12px 16px', fontSize: '13px', lineHeight: '1.8' }}
        >
          <div>1 bucket = 0.25 case</div>
          <div>1 &quot;1/3 container&quot; = 0.12 case</div>
        </div>
      </Popover>
    </PrepGuideContainer>
  )
}

export default PrepGuide
